/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useRef, useState, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { userErrorMessage } from "@/lib/user-error";
import { ingestFiles } from "@/lib/file-ingest";
import { PROJECT_DOCUMENT_ACCEPT, MAX_SHARE_ATTACHMENT_BYTES } from "@/lib/upload-limits";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useQuery } from "@apollo/client/react";
import { Tooltip } from "@/components/common/Tooltip";
import { Button } from "@/components/design/Button";
import { Disclosure } from "@/components/design/Disclosure";
import { NumberStepper } from "@/components/design/NumberStepper";
import { Switch } from "@/components/design/Switch";
import { Select } from "@/components/design/Select";
import { MatchTeamProfilesDocument } from "@/graphql/generated/operations";
import {
  UnlinkConfirmModal,
  VeroeffentlichenModal,
  VerknuepfenModal,
} from "./MatchModals";
import {
  DocumentPickerDialog,
  type PickedDocument,
} from "@/components/share/DocumentPickerDialog";
import { AskAssistantButton } from "@/components/chat/AskAssistantButton";
import { AgentPicker } from "./AgentPicker";
import { PowerProfileEditor } from "./PowerProfileEditor";
import { CandidateAssessmentPromptEditor } from "./CandidateAssessmentPromptEditor";
import { RequirementsPanel as TypedRequirementsPanel } from "./requirements/RequirementsPanel";
import { ExpertiseSection } from "./requirements/ExpertiseSection";
import { RecruitingExperience } from "./requirements/RecruitingExperience";
import { RecruitingQualifications } from "./requirements/RecruitingQualifications";
import { DetailsSection, DETAIL_TYPES } from "./requirements/DetailsSection";
import type { FileEntry, MatchData, SlideId } from "./match-design-types";
import type { LinkedSharedItem, MatchRole, UseMatchEditorResult } from "./use-match-editor";
import { Link, useNavigate } from "react-router";
import { BALANCED_TEAM_PROFILE_SEED_ID, DEFAULT_TEAM_PROFILE_ID, LEGACY_BALANCED_TEAM_PROFILE_ID, POWERS_WEIGHT_MIN, TEAM_SIZE_MAX, canPublishMatch, densifyElementWeights, isChallengeReady, makeIndividualTeamProfile, normalizePowersWeight } from "./match-design-rules";

export function ChallengeStep({
  data,
  update,
  editor,
  sharedItem,
  myRole,
  recruiting = false,
}: {
  data: MatchData;
  update: (patch: Partial<MatchData>) => void;
  recruiting?: boolean;
  editor: Pick<
    UseMatchEditorResult,
    | "linkSharedItem"
    | "unlinkSharedItem"
    | "publishAsSharedItem"
    | "attachProjectDocuments"
    | "removeProjectDocument"
  >;
  sharedItem: LinkedSharedItem;
  myRole: MatchRole | null;
  onNavigate: (slide: SlideId) => void;
}) {
  const intl = useIntl();
  const navigate = useNavigate();
  const [publishOpen, setPublishOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [unlinkOpen, setUnlinkOpen] = useState(false);
  const [serviceImportOpen, setServiceImportOpen] = useState(false);
  const [serviceImportError, setServiceImportError] = useState<string | null>(null);

  const canEditContent = myRole === "owner" || myRole === "admin" || myRole === "editor";
  const canManageSharedItem = canEditContent;
  const canPublish = canEditContent && canPublishMatch(data);
  const canGenerate = canEditContent && isChallengeReady(data);
  // Rank describes a role / open position with per-candidate requirements, not a team
  // project (@adr SPARK-0029-match-three-case-model-optimize-assess-rank@1) — the left-panel framing changes accordingly. Optimize is the only
  // mode with a team size for the AI to derive.
  const isRank = data.matchMode === "RANK";
  const isOptimize = data.matchMode !== "ASSESS" && data.matchMode !== "RANK";

  // Assistant frame (view-state only, no PII): mirror what the Challenge step shows. Team size is
  // Optimize-only (@adr SPARK-0029-match-three-case-model-optimize-assess-rank@1: Assess/Rank have no team to size), so it's omitted in those modes.
  const mustCount = data.conditions.filter((c) => c.requirement === "MUST").length;
  useAssistantContext([
    {
      description: "the match requirements the user is editing",
      value:
        `${data.conditions.length} requirements (${mustCount} MUSS / ${data.conditions.length - mustCount} KANN)` +
        (isOptimize ? `; team size ${data.teamSize}` : "") +
        `; ${data.projectDescription.trim() ? "project brief written" : "no project brief yet"}`,
    },
  ]);
  // KI-Signal: die Bewertungsstil-Box traegt den Signal-Schatten selbst in allen Modi
  // (data.aiEnabled, siehe CandidateAssessmentPromptEditor). Zusaetzlich tragen im Rank
  // die Rollenanforderungen den Schatten, weil sie bei aktiver KI Modell-Input sind
  // (UI_CONVENTIONS, Where AI lives). Team Powers bleibt ohne Signal (deterministischer
  // Power-Fit). Offen (spark #233): die Assess/Optimize-Teamanforderungen tragen den
  // Sektions-Schatten noch nicht.
  // transition in beiden Zustaenden, damit der Schatten beim Umschalten ein- UND
  // ausblendet statt hart zu wechseln.
  const aiSectionSignal = isRank ? `transition ${data.aiEnabled ? "smycles-ai-shadow" : ""}` : "";

  const resultRef = useRef<HTMLDivElement>(null);

  async function addPickedFromService(picked: PickedDocument[]) {
    setServiceImportError(null);
    // base64 length * 3/4 ≈ raw byte size; same heuristic ShareCreatePage uses.
    const rawBytes = (p: PickedDocument) => Math.floor(p.content.length * 0.75);
    const tooBig = picked.filter((p) => rawBytes(p) > MAX_SHARE_ATTACHMENT_BYTES);
    if (tooBig.length > 0) {
      const maxMb = Math.floor(MAX_SHARE_ATTACHMENT_BYTES / (1024 * 1024));
      setServiceImportError(
        intl.formatMessage(
          { id: "match.docs.tooLarge", defaultMessage: "Zu groß (max. {max} MB pro Datei): {names}" },
          { max: maxMb, names: tooBig.map((p) => p.name).join(", ") },
        ),
      );
    }
    const ok = picked.filter((p) => rawBytes(p) <= MAX_SHARE_ATTACHMENT_BYTES);
    if (ok.length === 0) return;
    try {
      await editor.attachProjectDocuments(
        ok.map((p) => ({ fileName: p.name, mimeType: p.mimeType, content: p.content })),
      );
    } catch (err) {
      setServiceImportError(userErrorMessage(intl, err));
    }
  }

  return (
    <div>
      <div className="grid grid-cols-1 items-start gap-6 @lg:grid-cols-2">
        <div className="box bg-surface p-6 pt-7">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <span className="text-[11px] tracking-[0.25em] text-ink/55">
              {isRank ? (
                <FormattedMessage id="match.challenge.describeRole" defaultMessage="Rolle beschreiben" />
              ) : (
                <FormattedMessage id="match.challenge.describeProject" defaultMessage="Projekt beschreiben" />
              )}
            </span>
            {canManageSharedItem && !sharedItem && (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="secondary"
                  size="compact"
                  className="gap-1.5 whitespace-nowrap"
                  onClick={() => setLinkOpen(true)}
                  title={intl.formatMessage({ id: "match.challenge.linkProjectTitle", defaultMessage: "Projekt mit existierendem Share-Item verknüpfen" })}
                >
                  <span>↘</span> <FormattedMessage id="match.challenge.linkBtn" defaultMessage="Verknüpfen" />
                </Button>
                <Button
                  variant="secondary"
                  size="compact"
                  className="gap-1.5 whitespace-nowrap"
                  onClick={() => setPublishOpen(true)}
                  disabled={!canPublish}
                  title={
                    canPublish
                      ? intl.formatMessage({ id: "match.publishToShare", defaultMessage: "Als Projekt in Share veröffentlichen" })
                      : intl.formatMessage({ id: "match.challenge.enterTitle", defaultMessage: "Titel eintragen" })
                  }
                >
                  <span>↗</span> <FormattedMessage id="match.challenge.publishBtn" defaultMessage="Veröffentlichen" />
                </Button>
              </div>
            )}
            {sharedItem && (
              <div className="flex flex-col items-end gap-0.5 text-right text-[10px] text-ink/40">
                <Link
                  to={`/share/discover/${sharedItem.id}`}
                  className="underline-offset-2 hover:text-ink hover:underline"
                  title={intl.formatMessage({ id: "match.challenge.openLinkedShare", defaultMessage: "Verknüpftes Share-Item öffnen" })}
                >
                  ↗ {sharedItem.name || intl.formatMessage({ id: "match.challenge.unnamedSharedItem", defaultMessage: "Unbenanntes Share-Item" })}
                </Link>
                {canManageSharedItem && (
                  <button
                    onClick={() => setUnlinkOpen(true)}
                    className="text-ink/30 underline-offset-2 transition hover:text-accent hover:underline"
                    title={intl.formatMessage({ id: "match.challenge.unlinkTitle", defaultMessage: "Verbindung zum Share-Item lösen" })}
                  >
                    <FormattedMessage id="match.challenge.unlink" defaultMessage="Verbindung lösen" />
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 @sm:grid-cols-2">
              <div className="bg-page box-flat p-5 transition focus-within:ring-1 focus-within:ring-accent/40">
                <div className="mb-1 text-[10px] tracking-[0.25em] text-ink/55">
                  {isRank ? (
                    <FormattedMessage id="match.challenge.roleTitleLabel" defaultMessage="Rollen-Titel" />
                  ) : (
                    <FormattedMessage id="match.challenge.projectTitleLabel" defaultMessage="Projekt-Titel" />
                  )}
                </div>
                <input
                  value={data.projectName}
                  onChange={(e) => update({ projectName: e.target.value })}
                  readOnly={!canEditContent}
                  placeholder={intl.formatMessage({ id: "match.titlePlaceholder", defaultMessage: "z. B. Set-Crew BTL · Q2 2026" })}
                  className="w-full bg-transparent text-xl font-medium outline-none placeholder:text-ink/30 read-only:opacity-70"
                />
              </div>

              <div className="bg-page box-flat p-5 transition focus-within:ring-1 focus-within:ring-accent/40">
                <div className="mb-1 text-[10px] tracking-[0.25em] text-ink/55">
                  <FormattedMessage id="match.fieldShortDesc" defaultMessage="Kurzbeschreibung" />
                </div>
                <input
                  value={data.projectShortDescription}
                  onChange={(e) => update({ projectShortDescription: e.target.value })}
                  readOnly={!canEditContent}
                  placeholder={intl.formatMessage({ id: "match.shortDescPlaceholder", defaultMessage: "In einem Satz: was, wofür?" })}
                  className="w-full bg-transparent text-sm outline-none placeholder:text-ink/30 read-only:opacity-70"
                />
              </div>
            </div>

            <div className="bg-page box-flat p-5 transition focus-within:ring-1 focus-within:ring-accent/40">
              <div className="mb-1 text-[10px] tracking-[0.25em] text-ink/55">
                <FormattedMessage id="match.fieldDetailedDesc" defaultMessage="Detaillierte Beschreibung" />
              </div>
              <textarea
                value={data.projectDescription}
                onChange={(e) => update({ projectDescription: e.target.value })}
                readOnly={!canEditContent}
                placeholder={intl.formatMessage({ id: "match.detailedPlaceholder", defaultMessage: "Kontext, Outcome, Constraints, Vorerfahrungen, alles was sonst relevant ist." })}
                rows={4}
                className="field-sizing-content w-full resize-none bg-transparent text-sm leading-relaxed outline-none placeholder:text-ink/30 read-only:opacity-70"
              />
              <div className="mt-2 flex items-center justify-between gap-3">
                {canEditContent ? (
                  <AskAssistantButton
                    variant="ghost"
                    prompt={intl.formatMessage({
                      id: "match.challenge.interview.seed",
                      defaultMessage: "Interviewe mich zu diesem Projekt, damit wir eine gute Projektbeschreibung bekommen.",
                    })}
                    label={data.projectDescription.trim().length === 0 ? (
                      <FormattedMessage id="match.challenge.interview.invite" defaultMessage="Unsicher, was rein soll? Lass dich vom Assistenten interviewen." />
                    ) : (
                      <FormattedMessage id="match.challenge.interview.refine" defaultMessage="Mit einem Interview verfeinern" />
                    )}
                  />
                ) : (
                  <span />
                )}
                <span className="text-[10px] tracking-wider text-ink/40 tabular-nums">
                  <FormattedMessage id="match.charsCount" defaultMessage="{count} Zeichen" values={{ count: data.projectDescription.length }} />
                </span>
              </div>
            </div>

            <div className="bg-page box-flat flex items-center justify-between gap-3 p-5">
              <div>
                <div className="mb-1 text-[10px] tracking-[0.25em] text-ink/55">
                  <FormattedMessage id="match.agenticAi" defaultMessage="Agentic AI" />
                </div>
                <div className="text-[12px] leading-snug text-ink/70">
                  <FormattedMessage id="match.agenticAiQuestion" defaultMessage="Soll dieses Match mit Agentic AI unterstützt werden?" />
                </div>
              </div>
              <Switch
                checked={data.agenticAi}
                onChange={(next) => update({ agenticAi: next })}
                disabled={!canEditContent}
                ariaLabel={data.agenticAi
                  ? intl.formatMessage({ id: "match.agenticAiOn", defaultMessage: "Agentic AI aktiv" })
                  : intl.formatMessage({ id: "match.agenticAiOff", defaultMessage: "Agentic AI aus" })}
                title={data.agenticAi
                  ? intl.formatMessage({ id: "match.agenticAiOn", defaultMessage: "Agentic AI aktiv" })
                  : intl.formatMessage({ id: "match.agenticAiOff", defaultMessage: "Agentic AI aus" })}
              />
            </div>

            <ProjectDocumentDropZone
              label={intl.formatMessage({ id: "match.documents", defaultMessage: "Dokumente" })}
              files={data.projectFiles}
              attachProjectDocuments={editor.attachProjectDocuments}
              removeProjectDocument={editor.removeProjectDocument}
              readOnly={!canEditContent}
              extraAction={
                canEditContent ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setServiceImportError(null);
                      setServiceImportOpen(true);
                    }}
                  >
                    <FormattedMessage id="share.create.fromDocuments" defaultMessage="From my documents" />
                  </Button>
                ) : undefined
              }
            />
            {serviceImportError ? (
              <div className="text-[12px] text-red-600">{serviceImportError}</div>
            ) : null}
          </div>

        </div>

        <div ref={resultRef} className="box scroll-mt-6 bg-surface p-6 pt-7">
          <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[11px] tracking-[0.25em] text-ink/55">
                <FormattedMessage id="match.challenge.requirements" defaultMessage="Anforderungen" />
              </div>
              {/* Hinweis als leise Bildunterschrift zum Titel statt eigener Box:
                  qualifiziert den ganzen Abschnitt, ohne Alarm-Gewicht. */}
              <p className="mt-1.5 field-meta max-w-2xl text-ink/45">
                <FormattedMessage
                  id="match.challenge.requirementsSharedInfo"
                  defaultMessage="Berücksichtigt werden nur Profilinformationen, die Kandidat:innen geteilt haben."
                />
              </p>
            </div>
            {canEditContent && (
              <Tooltip
                placement="left"
                content={
                  canGenerate
                    ? intl.formatMessage({ id: "match.challenge.aiDeriveHint", defaultMessage: "Öffnet den Assistenten, der Anforderungen aus deiner Beschreibung vorschlägt." })
                    : intl.formatMessage({ id: "match.challenge.aiFillFirstHint", defaultMessage: "Erst eine Beschreibung ausfüllen." })
                }
              >
                <AskAssistantButton
                  prompt={intl.formatMessage({
                    id: "match.challenge.generateRequirementsPrompt",
                    defaultMessage: "Schlag mir passende Anforderungen aus Titel, Kurz- und Detailbeschreibung vor.",
                  })}
                  label={<FormattedMessage id="match.challenge.generateWithAi" defaultMessage="Für mich generieren" />}
                  disabled={!canGenerate}
                />
              </Tooltip>
            )}
          </div>
          <div className="space-y-3">
            <CandidateAssessmentPromptEditor
              data={data}
              update={update}
              readOnly={!canEditContent}
              showStyleRegulators={isRank}
            />
            {data.agenticAi && (
              <AgentPicker
                agents={data.agents}
                projectText={`${data.projectName} ${data.projectShortDescription} ${data.projectDescription}`}
                onChange={(agents) => update({ agents })}
                readOnly={!canEditContent}
              />
            )}
            {recruiting ? (
              <>
                {/* Rank spiegelt das Optimize/Assess-Layout: alle Rollenanforderungen als
                    Untersektionen in EINER Karte, Fachwissen zuoberst (meist wichtigstes Kriterium).
                    Team Powers bleibt eine eigene Karte, wie in den anderen Modi (spark #230). */}
                <Disclosure
                  className={aiSectionSignal}
                  title={intl.formatMessage({
                    id: "match.roleReqsSection",
                    defaultMessage: "Rollenanforderungen",
                  })}
                  defaultOpen
                >
                  <div className="space-y-6">
                    <ExpertiseSection data={data} update={update} readOnly={!canEditContent} />
                    <DetailsSection
                      conditions={data.conditions.filter((c) => DETAIL_TYPES.has(c.__typename))}
                      onChange={(next) =>
                        update({
                          conditions: [
                            ...data.conditions.filter((c) => !DETAIL_TYPES.has(c.__typename)),
                            ...next,
                          ],
                        })
                      }
                      showRequirement
                      singlePerson
                      readOnly={!canEditContent}
                    />
                    <RecruitingExperience data={data} update={update} readOnly={!canEditContent} />
                    <RecruitingQualifications data={data} update={update} readOnly={!canEditContent} />
                  </div>
                </Disclosure>
                {/* Rank/Bewerbung: Powers als kandidaten-absolute Wuensche (Wire 0-1000, kein Muss/Kann-Gate),
                    speist den Power-Fit als zweite Achse im Ergebnis (spark #151, @adr SPARK-0030-match-result-scoring-atom-derived@1). */}
                <TeamPowersPanel data={data} update={update} />
              </>
            ) : (
              <>
                <RequirementsPanel data={data} update={update} readOnly={!canEditContent} />
                <TeamPowersPanel data={data} update={update} readOnly={!canEditContent} />
              </>
            )}
          </div>
        </div>
      </div>

      {linkOpen && (
        <VerknuepfenModal
          data={data}
          onConfirm={async ({ sharedItemId, copy }) => {
            await editor.linkSharedItem({ sharedItemId, copy });
          }}
          onClose={() => setLinkOpen(false)}
        />
      )}

      {unlinkOpen && sharedItem && (
        <UnlinkConfirmModal
          sharedItemName={sharedItem.name || intl.formatMessage({ id: "match.challenge.unnamedSharedItem", defaultMessage: "Unbenanntes Share-Item" })}
          onConfirm={async () => {
            await editor.unlinkSharedItem();
          }}
          onClose={() => setUnlinkOpen(false)}
        />
      )}

      {publishOpen && (
        <VeroeffentlichenModal
          projectName={data.projectName}
          onPublish={async ({ openEditor }) => {
            const newId = await editor.publishAsSharedItem();
            if (openEditor) navigate(`/share/edit/${newId}`);
            return newId;
          }}
          onClose={() => setPublishOpen(false)}
        />
      )}

      <DocumentPickerDialog
        open={serviceImportOpen}
        kind="file"
        onClose={() => setServiceImportOpen(false)}
        onPick={(picked) => void addPickedFromService(picked)}
      />

    </div>
  );
}

// One file in the dropzone — either confirmed server-side (has backdropFileId,
// rendered as `state: "ready"`) or in-flight on the client (has clientId,
// `state: "uploading" | "error"`). Server entries can be detached; client
// entries can be retried (error) or dismissed (drops local state).
interface ProjectDocumentRow {
  key: string;
  name: string;
  size: number;
  state: "ready" | "uploading" | "error";
  errorMessage?: string;
  // ready → detach via genesis; error/uploading → drop the local pending entry.
  onRemove?: () => void;
  onRetry?: () => void;
}

interface PendingUpload {
  clientId: string;
  fileName: string;
  size: number;
  mimeType: string;
  state: "uploading" | "error";
  errorMessage?: string;
  // Retained while in-flight or in error so a retry doesn't re-read the disk.
  payload: { fileName: string; mimeType: string; content: string };
}

function ProjectDocumentDropZone({
  label,
  files,
  attachProjectDocuments,
  removeProjectDocument,
  extraAction,
  readOnly = false,
}: {
  label: string;
  files: FileEntry[];
  attachProjectDocuments: (
    files: Array<{ fileName: string; mimeType: string; content: string }>,
  ) => Promise<void>;
  removeProjectDocument: (backdropFileId: string) => Promise<void>;
  extraAction?: ReactNode;
  readOnly?: boolean;
}) {
  const intl = useIntl();
  const [dragOver, setDragOver] = useState(false);
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([]);
  const [topLevelError, setTopLevelError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  async function ingest(fileList: FileList | null) {
    let result;
    try {
      result = await ingestFiles(fileList, { maxBytes: MAX_SHARE_ATTACHMENT_BYTES });
    } catch (err) {
      setTopLevelError(userErrorMessage(intl, err));
      return;
    }

    if (result.rejected.length > 0) {
      const maxMb = Math.floor(MAX_SHARE_ATTACHMENT_BYTES / (1024 * 1024));
      setTopLevelError(
        intl.formatMessage(
          {
            id: "match.docs.tooLarge",
            defaultMessage: "Zu groß (max. {max} MB pro Datei): {names}",
          },
          { max: maxMb, names: result.rejected.map((r) => r.file.name).join(", ") },
        ),
      );
    } else {
      setTopLevelError(null);
    }
    if (result.accepted.length === 0) return;

    const payloads = result.accepted.map((f) => ({
      fileName: f.name,
      mimeType: f.mimeType,
      content: f.content,
    }));
    const created: PendingUpload[] = result.accepted.map((f, i) => ({
      clientId: crypto.randomUUID(),
      fileName: f.name,
      size: f.size,
      mimeType: f.mimeType,
      state: "uploading",
      payload: payloads[i]!,
    }));
    const createdIds = new Set(created.map((c) => c.clientId));
    setPendingUploads((prev) => [...prev, ...created]);

    try {
      await attachProjectDocuments(payloads);
      // Success: genesis returned the updated Match; commitServerTruth's
      // refetch updates `files` (server-side) with the new entries. Drop
      // the local pending rows so they don't double-render alongside.
      setPendingUploads((prev) => prev.filter((p) => !createdIds.has(p.clientId)));
    } catch (err) {
      const msg = userErrorMessage(intl, err);
      setPendingUploads((prev) =>
        prev.map((p) =>
          createdIds.has(p.clientId)
            ? { ...p, state: "error" as const, errorMessage: msg }
            : p,
        ),
      );
    }
  }

  async function retry(clientId: string) {
    const entry = pendingUploads.find((p) => p.clientId === clientId);
    if (!entry) return;
    setPendingUploads((prev) =>
      prev.map((p) =>
        p.clientId === clientId
          ? { ...p, state: "uploading" as const, errorMessage: undefined }
          : p,
      ),
    );
    try {
      await attachProjectDocuments([entry.payload]);
      setPendingUploads((prev) => prev.filter((p) => p.clientId !== clientId));
    } catch (err) {
      const msg = userErrorMessage(intl, err);
      setPendingUploads((prev) =>
        prev.map((p) =>
          p.clientId === clientId
            ? { ...p, state: "error" as const, errorMessage: msg }
            : p,
        ),
      );
    }
  }

  function dismissPending(clientId: string) {
    setPendingUploads((prev) => prev.filter((p) => p.clientId !== clientId));
  }

  async function detachServerFile(backdropFileId: string) {
    try {
      await removeProjectDocument(backdropFileId);
    } catch (err) {
      setTopLevelError(userErrorMessage(intl, err));
    }
  }

  // Merged display: server-confirmed entries first (stable across renders;
  // their backdropFileId is the key), then in-flight client entries.
  const rows: ProjectDocumentRow[] = [
    ...files.map<ProjectDocumentRow>((f) => ({
      key: f.backdropFileId ?? `noid:${f.name}`,
      name: f.name,
      size: f.size,
      state: "ready",
      onRemove:
        !readOnly && f.backdropFileId
          ? () => void detachServerFile(f.backdropFileId!)
          : undefined,
    })),
    ...pendingUploads.map<ProjectDocumentRow>((p) => ({
      key: `pending:${p.clientId}`,
      name: p.fileName,
      size: p.size,
      state: p.state,
      errorMessage: p.errorMessage,
      onRemove: !readOnly ? () => dismissPending(p.clientId) : undefined,
      onRetry: p.state === "error" ? () => void retry(p.clientId) : undefined,
    })),
  ];

  return (
    <div className="bg-page box-flat p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="text-[10px] tracking-[0.25em] text-ink/55">{label}</div>
        {extraAction ? <div className="flex items-center">{extraAction}</div> : null}
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        // Not "*/*": the picker is the first gate, and nothing downstream checks the type — not the
        // handler, not the BFF, not genesis. Documents plus the raster formats a brief can carry.
        accept={PROJECT_DOCUMENT_ACCEPT}
        onChange={(e) => {
          void ingest(e.target.files);
          // Reset so the same file can be re-selected after error.
          e.target.value = "";
        }}
        className="hidden"
      />

      {!readOnly && (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            void ingest(e.dataTransfer.files);
          }}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={intl.formatMessage({ id: "match.addDocumentsAriaLabel", defaultMessage: "Dokumente hinzufügen" })}
          className={`mt-2 cursor-pointer rounded-md border-2 border-dashed transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 ${
            dragOver ? "border-accent bg-ink/[0.06]" : "border-ink/15 hover:border-accent/40"
          }`}
        >
          <div className="flex items-center justify-center gap-2 py-4 text-center text-[12px] text-ink/55">
            <span aria-hidden className="text-ink/30">📎</span>
            <FormattedMessage
              id="match.dropFiles"
              defaultMessage="Dateien <b>hierher ziehen</b> oder klicken"
              values={{ b: (chunks) => <span className="font-medium">{chunks}</span> }}
            />
          </div>
        </div>
      )}

      {rows.length > 0 ? (
        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
          {rows.map((row) => (
            <ProjectDocumentTile key={row.key} row={row} />
          ))}
        </div>
      ) : readOnly ? (
        <div className="mt-2 flex items-center justify-center gap-2 py-4 text-center text-[12px] text-ink/55">
          <span aria-hidden className="text-ink/30">📎</span>
          <FormattedMessage id="match.challenge.noFilesYet" defaultMessage="Noch keine Dateien." />
        </div>
      ) : null}

      {topLevelError && (
        <div role="alert" className="mt-2 text-[11px] text-accent">
          {topLevelError}
        </div>
      )}
    </div>
  );
}

function ProjectDocumentTile({ row }: { row: ProjectDocumentRow }) {
  const intl = useIntl();
  const kb = row.size ? Math.round(row.size / 1024) : 0;
  const isUploading = row.state === "uploading";
  const isError = row.state === "error";
  return (
    <div
      className={`bg-page box-flat group relative overflow-hidden ${
        isError ? "ring-1 ring-accent" : ""
      }`}
    >
      {row.onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            row.onRemove?.();
          }}
          className="absolute right-1.5 top-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-ink/70 text-[10px] text-page opacity-0 transition group-hover:opacity-100"
          title={intl.formatMessage({ id: "common.remove", defaultMessage: "Entfernen" })}
        >
          ✕
        </button>
      )}
      <div className="relative flex aspect-[4/3] items-center justify-center text-2xl text-ink/40">
        📄
        {isUploading && (
          <span
            aria-hidden
            className="absolute inset-0 flex items-center justify-center bg-page/70"
          >
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent text-ink/55" />
          </span>
        )}
        {isError && (
          <span
            aria-hidden
            className="absolute inset-0 flex items-center justify-center bg-page/70 text-base text-accent"
          >
            ⚠
          </span>
        )}
      </div>
      <div className="px-2 py-1.5">
        <div className="truncate text-[11px] font-medium">{row.name}</div>
        <div className="text-[10px] text-ink/45">
          {isUploading ? (
            <FormattedMessage id="match.docs.uploading" defaultMessage="Wird hochgeladen…" />
          ) : isError ? (
            <span className="text-accent">
              {row.errorMessage ??
                intl.formatMessage({
                  id: "match.docs.uploadFailed",
                  defaultMessage: "Upload fehlgeschlagen.",
                })}
            </span>
          ) : kb < 1024 ? (
            `${kb} KB`
          ) : (
            `${(kb / 1024).toFixed(1)} MB`
          )}
        </div>
        {isError && row.onRetry && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              row.onRetry?.();
            }}
            className="mt-1 whitespace-nowrap text-[10px] tracking-[0.25em] text-ink/55 hover:text-ink"
          >
            <FormattedMessage id="match.docs.retry" defaultMessage="↻ Erneut versuchen" />
          </button>
        )}
      </div>
    </div>
  );
}

function RequirementsPanel({
  data,
  update,
  readOnly = false,
}: {
  data: MatchData;
  update: (patch: Partial<MatchData>) => void;
  readOnly?: boolean;
}) {
  const intl = useIntl();
  // Team-size is Optimize-only (@adr SPARK-0029-match-three-case-model-optimize-assess-rank@1): Assess/Rank evaluate a fixed candidate
  // pool and have no team to size. matchMode null = legacy Optimize.
  const isOptimize = data.matchMode !== "ASSESS" && data.matchMode !== "RANK";
  return (
    <div className="space-y-3">
      {isOptimize && (
        <div className="bg-page box-flat flex items-center justify-between gap-3 px-5 py-2.5">
          <span className="text-sm font-medium">
            <FormattedMessage id="match.teamSizeSection" defaultMessage="Größe des Teams" />
          </span>
          <NumberStepper
            value={data.teamSize}
            // Round + bound here rather than inside NumberStepper: the stepper accepts decimals on
            // purpose (RecruitingExperience uses step={0.5}). An unrounded value here is unreadable —
            // design-codec types teamSize as z.number().int() and parseDesign THROWS on a mismatch, so
            // "2,5" made the match editor unopenable.
            onChange={(next) => update({ teamSize: Math.round(next) })}
            min={1}
            max={TEAM_SIZE_MAX}
            disabled={readOnly}
            ariaLabel={intl.formatMessage({ id: "match.teamSizeSection", defaultMessage: "Größe des Teams" })}
          />
        </div>
      )}

      <TypedRequirementsPanel data={data} update={update} readOnly={readOnly} />
    </div>
  );
}

function TeamPowersPanel({
  data,
  update,
  readOnly = false,
}: {
  data: MatchData;
  update: (patch: Partial<MatchData>) => void;
  readOnly?: boolean;
}) {
  const intl = useIntl();

  return (
    <Disclosure title={intl.formatMessage({ id: "match.teamPowers", defaultMessage: "Power Scorecard" })} defaultOpen>
      <PowersPicker
        profile={data.teamProfile}
        onChange={(profile) => update({ teamProfile: profile })}
        readOnly={readOnly}
      />
    </Disclosure>
  );
}


function PowersPicker({
  profile,
  onChange,
  readOnly = false,
}: {
  profile: MatchData["teamProfile"];
  onChange: (profile: MatchData["teamProfile"]) => void;
  readOnly?: boolean;
}) {
  const intl = useIntl();
  const { data, loading, error } = useQuery(MatchTeamProfilesDocument, {
    variables: { input: { first: 50 } as any },
  });
  const profiles = useMemo(() => {
    return ((data as any)?.teamProfiles?.nodes ?? []).map((p: any) => {
      const enumWeights: Record<string, number> = {};
      for (const { element, weight } of p.weights ?? []) {
        if (element) {
          enumWeights[element] = normalizePowersWeight(weight);
        }
      }
      return {
        id: p.id as string,
        name: (p.name ?? p.id) as string,
        label:
          p.localizedName?.localized ||
          p.localizedName?.english ||
          p.name ||
          p.id,
        // Densify at the picker seam so downstream consumers (preset apply,
        // Individuell switch, reset, divergence check) never see a sparse
        // preset. Missing keys default to 0 (preset author intent).
        // Without this, switching to Individuell would carry the sparse map
        // forward, then dataToDesign would fill the gaps with 5 — inflating
        // previously-excluded Powers and silently changing the brief.
        elementWeights: densifyElementWeights(enumWeights, POWERS_WEIGHT_MIN),
      };
    });
  }, [data]);

  // Designs saved before genesis#211 reference the old client-side synthetic
  // "Ausgewogen"; the seeded row is the same profile, so map the id onto it and
  // the dropdown keeps showing a selection for those matches.
  const savedProfileId = profile?.id ?? DEFAULT_TEAM_PROFILE_ID;
  const selectedProfileId =
    savedProfileId === LEGACY_BALANCED_TEAM_PROFILE_ID
      ? BALANCED_TEAM_PROFILE_SEED_ID
      : savedProfileId;

  // A design can reference a profile the list no longer carries: admins can now
  // delete Power Profiles (spark#218), and the list is still loading on first
  // paint. Carry the stored one as its own option under the name saved in the
  // design, otherwise the Select falls back to rendering the first option and
  // the brief silently reads "Individuell" while still holding those weights.
  const pickerProfiles = useMemo(() => {
    if (!profile || selectedProfileId === DEFAULT_TEAM_PROFILE_ID) return profiles;
    if (profiles.some((p: any) => p.id === selectedProfileId)) return profiles;
    const label = profile.name || selectedProfileId;
    return [...profiles, { id: selectedProfileId, name: profile.name, label, elementWeights: profile.elementWeights }];
  }, [profiles, profile, selectedProfileId]);

  // Divergence: is the live profile's Importance shape identical to the source
  // preset's saved shape? If not, the dropdown label gets a "· geändert"
  // suffix so the user knows they've drifted. Reselecting the preset replaces
  // the weights — the dropdown is its own reset path. See @adr SPARK-0033-power-weights-word-scale-plus-exact-field@1.
  const sourcePreset = profile
    ? pickerProfiles.find((p: any) => p.id === selectedProfileId)
    : null;
  const isCustom = profile?.id === DEFAULT_TEAM_PROFILE_ID;
  const isModified = useMemo(() => {
    if (!profile || !sourcePreset) return false;
    const a = profile.elementWeights;
    const b = sourcePreset.elementWeights as Record<string, number>;
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const k of keys) {
      if ((a[k] ?? 0) !== (b[k] ?? 0)) return true;
    }
    return false;
  }, [profile, sourcePreset]);

  function setProfile(id: string) {
    if (id === DEFAULT_TEAM_PROFILE_ID) {
      // Switching to Individuell keeps the current weights – picking it is the
      // user saying "I'm no longer following a preset", not "reset my shape".
      // The Zurücksetzen button is the explicit reset path.
      onChange(makeIndividualTeamProfile(profile?.elementWeights));
      return;
    }
    const sel = pickerProfiles.find((p: any) => p.id === id);
    if (!sel) return;
    onChange({
      id: sel.id,
      name: sel.name,
      elementWeights: { ...sel.elementWeights },
    });
  }

  function resetToPreset() {
    if (!profile || !sourcePreset) return;
    onChange({
      id: sourcePreset.id,
      name: sourcePreset.name,
      elementWeights: { ...(sourcePreset.elementWeights as Record<string, number>) },
    });
  }

  const canReset = !readOnly && isModified && !isCustom && Boolean(sourcePreset);

  const modifiedSuffix = intl.formatMessage({
    id: "match.teamProfileModifiedSuffix",
    defaultMessage: " · geändert",
  });
  const customLabel = intl.formatMessage({
    id: "match.teamProfileCustom",
    defaultMessage: "Individuell",
  });

  return (
    <div className="space-y-5">
      <div>
        <div className="mb-1 text-[10px] tracking-[0.2em] text-ink/45">
          <FormattedMessage id="match.teamProfile" defaultMessage="Selection" />
        </div>
        <Select
          value={selectedProfileId}
          onChange={(next) => setProfile(next)}
          disabled={loading || readOnly}
          hideEmpty
          options={[
            { value: DEFAULT_TEAM_PROFILE_ID, label: customLabel },
            ...pickerProfiles.map((p: any) => ({
              value: p.id,
              label: isModified && selectedProfileId === p.id ? `${p.label}${modifiedSuffix}` : p.label,
            })),
          ]}
        />
        {error && (
          <div className="mt-1 text-[11px] text-red-600">
            <FormattedMessage id="match.profilesLoadError" defaultMessage="Profile laden: {message}" values={{ message: userErrorMessage(intl, error) }} />
          </div>
        )}
      </div>

      {profile ? <PowerProfileEditor profile={profile} onChange={onChange} readOnly={readOnly} /> : null}

      {canReset ? (
        <div className="flex items-center justify-end">
          <button
            onClick={resetToPreset}
            className="text-[11px] tracking-widest text-ink/55 underline-offset-2 hover:text-ink hover:underline"
          >
            <FormattedMessage id="match.editor.reset" defaultMessage="Zurücksetzen" />
          </button>
        </div>
      ) : null}
    </div>
  );
}
